<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import { ExternalLink } from 'lucide-vue-next'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import { Badge } from '@/components/ui/badge'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import type { PaginatedResponse, FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import {
    ActivitiesService,
    type TActivity,
    ActivityTypeEnum,
    OrganisationsService,
    type CustomerOrganisation
  } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import { ROUTES } from '@/router/routes.ts'
  import { formatDistanceToNow } from 'date-fns'
  import { de } from 'date-fns/locale'

  const { t } = useTranslation({
    en: {
      Activities: 'Activities',
      pageDescription: 'View all incoming activities and updates',
      TimeStamp: 'Received',
      Type: 'Type',
      Activity: 'Activity',
      Person: 'Person',
      Clerk: 'Clerk',
      Action: 'Action',
      OpenCase: 'Open',
      AllActivities: 'All Activities',
      UnseenActivities: 'Unseen Activities',
      UnseenActivity: 'Unseen Activity',
      AllTypes: 'All Types',
      AllClerks: 'All Clerks',
      NotAssigned: 'Not assigned',
      NoClientInfo: 'No client information',
      CaseCreated: 'Inquiry',
      DocumentSigned: 'Document',
      EmailReceived: 'Email received',
      EmailSent: 'Email sent',
      QuestionnaireClosed: 'Questionnaire',
      AppointmentCreated: 'Appointment',
      NewInquiryReceived: 'A new inquiry has been received.',
      DocumentReceived: 'The document {title} has been received.',
      DocumentReceivedNoTitle: 'A document has been received.',
      EmailReceivedFrom: 'An email has been received from {email}.',
      EmailReceivedNoSender: 'An email has been received.',
      EmailSentTo: 'An email was sent to {email}.',
      EmailSentNoRecipient: 'An email was sent.',
      QuestionnaireReceived: 'The questionnaire {title} has been received.',
      QuestionnaireReceivedNoTitle: 'A questionnaire has been received.',
      AppointmentConfirmed: 'An appointment for {date} at {time} has been confirmed.',
      AppointmentProposed: 'An appointment proposal for {date} at {time} has been received.'
    },
    de: {
      Activities: 'Aktivitäten',
      pageDescription: 'Alle eingehenden Aktivitäten und Aktualisierungen anzeigen',
      TimeStamp: 'Eingegangen',
      Type: 'Typ',
      Activity: 'Aktivität',
      Person: 'Person',
      Clerk: 'Sachbearbeiter',
      Action: 'Aktion',
      OpenCase: 'Öffnen',
      AllActivities: 'Alle Aktivitäten',
      UnseenActivities: 'Ungesehene Aktivitäten',
      UnseenActivity: 'Ungesehene Aktivität',
      AllTypes: 'Alle Typen',
      AllClerks: 'Alle Sachbearbeiter',
      NotAssigned: 'Nicht zugewiesen',
      NoClientInfo: 'Keine Kundeninformation',
      CaseCreated: 'Anfrage',
      DocumentSigned: 'Dokument',
      EmailReceived: 'E-Mail empfangen',
      EmailSent: 'E-Mail gesendet',
      QuestionnaireClosed: 'Fragebogen',
      AppointmentCreated: 'Terminbuchung',
      NewInquiryReceived: 'Eine neue Anfrage ist eingegangen.',
      DocumentReceived: 'Das Dokument {title} ist eingegangen.',
      DocumentReceivedNoTitle: 'Ein Dokument ist eingegangen.',
      EmailReceivedFrom: 'Eine E-Mail ist von {email} eingegangen.',
      EmailReceivedNoSender: 'Eine E-Mail ist eingegangen.',
      EmailSentTo: 'Eine E-Mail wurde an {email} gesendet.',
      EmailSentNoRecipient: 'Eine E-Mail wurde gesendet.',
      QuestionnaireReceived: 'Der Fragebogen {title} ist eingegangen.',
      QuestionnaireReceivedNoTitle: 'Ein Fragebogen ist eingegangen.',
      AppointmentConfirmed: 'Ein Termin für den {date} um {time} Uhr ist eingegangen.',
      AppointmentProposed: 'Ein Terminvorschlag für den {date} um {time} Uhr ist eingegangen.'
    }
  })

  const tableRef = ref()
  const selectedState = ref<'ALL' | 'UNSEEN'>('ALL')
  const selectedActivityType = ref('')
  const selectedClerk = ref('')
  const unseenCount = ref(0)
  const org = ref<CustomerOrganisation>()

  const activityTypeOptions = [
    { value: ActivityTypeEnum.CASE_CREATED, label: 'CaseCreated' },
    { value: ActivityTypeEnum.DOCUMENT_SIGNED, label: 'DocumentSigned' },
    { value: ActivityTypeEnum.EMAIL_RECEIVED, label: 'EmailReceived' },
    { value: ActivityTypeEnum.EMAIL_SENT, label: 'EmailSent' },
    { value: ActivityTypeEnum.QUESTIONNAIRE_CLOSED, label: 'QuestionnaireClosed' },
    { value: ActivityTypeEnum.APPOINTMENT_CREATED, label: 'AppointmentCreated' }
  ]

  const clerkOptions = computed(() => {
    if (!org.value?.users) return []
    return org.value.users.map((user: any) => ({
      value: user.uuid,
      label: `${user.first_name} ${user.last_name}`.trim()
    }))
  })

  const filterableFields = computed(() => [
    {
      key: 'activityType',
      label: t('Type'),
      type: 'select' as const,
      value: selectedActivityType.value,
      options: [
        { label: t('AllTypes'), value: '' },
        ...activityTypeOptions
          .map((option) => ({
            label: t(option.label),
            value: option.value
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      ]
    },
    {
      key: 'clerk',
      label: t('Clerk'),
      type: 'select' as const,
      value: selectedClerk.value,
      options: [
        { label: t('AllClerks'), value: '' },
        ...[...clerkOptions.value].sort((a, b) => a.label.localeCompare(b.label))
      ]
    }
  ])

  watch([selectedActivityType, selectedClerk], () => {
    if (tableRef.value) {
      tableRef.value.refreshData()
    }
  })

  const fetchActivities = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<TActivity>> => {
    try {
      const activityTypeFilter = options?.filters?.find((f) => f.key === 'activityType')
      const clerkFilter = options?.filters?.find((f) => f.key === 'clerk')

      const params: any = {
        limit: options?.limit,
        offset: options?.offset,
        state: selectedState.value,
        ordering: '-created_at'
      }

      // Add search parameter if provided
      if (options?.search) {
        params.search = options.search
      }

      const activityType = activityTypeFilter?.value || selectedActivityType.value
      if (activityType) {
        params.activityType = activityType
      }

      const clerk = clerkFilter?.value || selectedClerk.value
      if (clerk) {
        params.clerk = clerk
      }

      const response = await ActivitiesService.activitiesList(params)

      return response as unknown as PaginatedResponse<TActivity>
    } catch (error) {
      console.error('Error fetching activities:', error)
      throw error
    }
  }

  const fetchUnseenCount = async () => {
    try {
      const response = await ActivitiesService.activitiesUnseenCountRetrieve()
      unseenCount.value = (response as { count: number }).count
    } catch (error) {
      console.error('Error fetching unseen count:', error)
    }
  }

  const fetchCurrentOrg = async () => {
    try {
      org.value = (await OrganisationsService.organisationsRetrieve({
        uuid: 'current'
      })) as CustomerOrganisation
    } catch (error) {
      console.error('Error fetching org:', error)
    }
  }

  onMounted(() => {
    fetchUnseenCount()
    fetchCurrentOrg()
  })

  watch(selectedState, () => {
    if (tableRef.value) {
      tableRef.value.refreshData()
    }
  })

  const changeState = (state: 'ALL' | 'UNSEEN') => {
    selectedState.value = state
  }

  const formatTimestamp = (timestamp: string) => {
    return formatDistanceToNow(new Date(timestamp), {
      addSuffix: true,
      locale: de
    })
  }

  const handleOpenCase = (activity: TActivity) => {
    const caseUuid = (activity as any).case_uuid
    if (caseUuid) {
      window.location.href = ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(':uuid', caseUuid)
    }
  }

  const getActivityTypeLabel = (activityType: ActivityTypeEnum) => {
    switch (activityType) {
      case ActivityTypeEnum.CASE_CREATED:
        return t('CaseCreated')
      case ActivityTypeEnum.DOCUMENT_SIGNED:
        return t('DocumentSigned')
      case ActivityTypeEnum.EMAIL_RECEIVED:
        return t('EmailReceived')
      case ActivityTypeEnum.EMAIL_SENT:
        return t('EmailSent')
      case ActivityTypeEnum.QUESTIONNAIRE_CLOSED:
        return t('QuestionnaireClosed')
      case ActivityTypeEnum.APPOINTMENT_CREATED:
        return t('AppointmentCreated')
      default:
        return activityType
    }
  }

  const getActivityTypeBadgeClass = (activityType: ActivityTypeEnum) => {
    switch (activityType) {
      case ActivityTypeEnum.CASE_CREATED:
        return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
      case ActivityTypeEnum.DOCUMENT_SIGNED:
        return 'bg-rose-50 text-rose-700 ring-rose-600/20'
      case ActivityTypeEnum.EMAIL_RECEIVED:
        return 'bg-sky-50 text-sky-700 ring-sky-600/20'
      case ActivityTypeEnum.EMAIL_SENT:
        return 'bg-blue-50 text-blue-700 ring-blue-600/20'
      case ActivityTypeEnum.QUESTIONNAIRE_CLOSED:
        return 'bg-violet-50 text-violet-700 ring-violet-600/20'
      case ActivityTypeEnum.APPOINTMENT_CREATED:
        return 'bg-amber-50 text-amber-700 ring-amber-600/20'
      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'
    }
  }

  const getActivityData = (activity: TActivity) => {
    const act = activity as any
    switch (activity.activity_type) {
      case ActivityTypeEnum.CASE_CREATED:
        return { text: t('NewInquiryReceived'), emphasize: null }
      case ActivityTypeEnum.DOCUMENT_SIGNED: {
        const title = act.document_title?.trim()
        if (!title) {
          return { text: t('DocumentReceivedNoTitle'), emphasize: null }
        }
        // Split the translation to get parts before and after the title
        const parts = t('DocumentReceived', { title: '{TITLE}' }).split('{TITLE}')
        return {
          textBefore: parts[0] || '',
          emphasize: title,
          textAfter: parts[1] || ''
        }
      }
      case ActivityTypeEnum.EMAIL_RECEIVED: {
        const email = act.email_from?.trim()
        if (!email) {
          return { text: t('EmailReceivedNoSender'), emphasize: null }
        }
        const parts = t('EmailReceivedFrom', { email: '{EMAIL}' }).split('{EMAIL}')
        return {
          textBefore: parts[0] || '',
          emphasize: email,
          textAfter: parts[1] || ''
        }
      }
      case ActivityTypeEnum.EMAIL_SENT: {
        // email_to is an array of email addresses
        let email = ''
        if (Array.isArray(act.email_to)) {
          const emailArray = act.email_to.filter((e: string) => e && e.trim())
          email = emailArray.join(', ').trim()
        } else if (typeof act.email_to === 'string') {
          email = act.email_to.trim()
        }

        if (!email) {
          return { text: t('EmailSentNoRecipient'), emphasize: null }
        }
        const parts = t('EmailSentTo', { email: '{EMAIL}' }).split('{EMAIL}')
        return {
          textBefore: parts[0] || '',
          emphasize: email,
          textAfter: parts[1] || ''
        }
      }
      case ActivityTypeEnum.QUESTIONNAIRE_CLOSED: {
        const title = act.questionnaire_title?.trim()
        if (!title) {
          return { text: t('QuestionnaireReceivedNoTitle'), emphasize: null }
        }
        const parts = t('QuestionnaireReceived', { title: '{TITLE}' }).split('{TITLE}')
        return {
          textBefore: parts[0] || '',
          emphasize: title,
          textAfter: parts[1] || ''
        }
      }
      case ActivityTypeEnum.APPOINTMENT_CREATED:
        if (act.appointment_start) {
          const date = new Date(act.appointment_start)
          const dateStr = date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          const timeStr = date.toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit'
          })
          if (act.appointment_is_confirmed) {
            return {
              text: t('AppointmentConfirmed', { date: dateStr, time: timeStr }),
              emphasize: null
            }
          }
          return {
            text: t('AppointmentProposed', { date: dateStr, time: timeStr }),
            emphasize: null
          }
        }
        return { text: '', emphasize: null }
      default:
        return { text: '', emphasize: null }
    }
  }

  const getClerkName = (activity: TActivity) => {
    const act = activity as any
    if (act.clerk_first_name || act.clerk_last_name) {
      return `${act.clerk_first_name || ''} ${act.clerk_last_name || ''}`.trim()
    }
    return null
  }

  const getClerkInitials = (activity: TActivity) => {
    const act = activity as any
    const first = act.clerk_first_name?.[0] || ''
    const last = act.clerk_last_name?.[0] || ''
    return (first + last).toUpperCase() || '?'
  }
</script>

<template>
  <SpaCompatComponent>
    <div class="min-h-screen pb-20 pt-5">
      <div class="mx-auto space-y-6">
        <!-- Header -->
        <div class="flex items-center justify-between">
          <div>
            <h1
              data-cy="activities-page-title"
              class="text-2xl font-semibold text-gray-900"
            >
              {{ t('Activities') }}
            </h1>
            <p class="mt-1 text-sm text-gray-500">{{ t('pageDescription') }}</p>
          </div>

          <!-- State Toggle -->
          <div
            data-cy="activities-state-filter"
            class="flex items-center gap-2"
          >
            <div class="inline-flex rounded-lg border border-gray-200 bg-white p-1">
              <button
                data-cy="activities-filter-all"
                :class="[
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  selectedState === 'ALL'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                ]"
                @click="changeState('ALL')"
              >
                {{ t('AllActivities') }}
              </button>
              <button
                data-cy="activities-filter-unseen"
                :class="[
                  'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  selectedState === 'UNSEEN'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                ]"
                @click="changeState('UNSEEN')"
              >
                <span
                  v-if="unseenCount > 0"
                  data-cy="unseen-count-badge"
                  class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-violet-500 px-1.5 text-xs
                    font-medium text-white"
                >
                  {{ unseenCount }}
                </span>
                {{ unseenCount === 1 ? t('UnseenActivity') : t('UnseenActivities') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div
          data-cy="activities-table-container"
          class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5"
        >
          <PaginatedTableView
            ref="tableRef"
            :fetchData="fetchActivities"
            searchable-fields="Activity"
            :filterable-fields="filterableFields"
            pagination-nav-data-cy="pagination-nav"
            pagination-prev-data-cy="pagination-prev"
            pagination-next-data-cy="pagination-next"
            pagination-input-data-cy="pagination-input"
          >
            <template #table-head>
              <TableCell class="w-[160px] py-4">{{ t('TimeStamp') }}</TableCell>
              <TableCell class="w-[150px] py-4">{{ t('Type') }}</TableCell>
              <TableCell class="w-full py-4">{{ t('Activity') }}</TableCell>
              <TableCell class="w-[220px] py-4">{{ t('Person') }}</TableCell>
              <TableCell class="w-[250px] py-4">{{ t('Clerk') }}</TableCell>
              <TableCell class="w-[100px] py-4"></TableCell>
            </template>

            <template #table-row="{ items }">
              <TableRow
                v-for="activity in items"
                :key="activity.uuid"
                :data-cy="`activity-row-${activity.uuid}`"
                class="group cursor-pointer border-b border-gray-100 hover:bg-gray-50/50"
                @click="handleOpenCase(activity)"
              >
                <!-- Timestamp with Unseen Indicator -->
                <TableCell
                  data-cy="cell-timestamp"
                  class="py-4"
                >
                  <div class="flex items-center gap-2">
                    <span
                      v-if="activity.seen_at === null"
                      data-cy="indicator-unseen"
                      class="inline-block h-2 w-2 shrink-0 rounded-full bg-violet-500"
                    ></span>
                    <span
                      v-else
                      class="inline-block h-2 w-2 shrink-0"
                    ></span>
                    <span class="whitespace-nowrap text-sm text-gray-600">
                      {{ formatTimestamp(activity.created_at) }}
                    </span>
                  </div>
                </TableCell>

                <!-- Activity Type Badge -->
                <TableCell data-cy="cell-type">
                  <Badge
                    :class="`${getActivityTypeBadgeClass(activity.activity_type)} whitespace-nowrap`"
                  >
                    {{ getActivityTypeLabel(activity.activity_type) }}
                  </Badge>
                </TableCell>

                <!-- Activity Description -->
                <TableCell
                  data-cy="cell-description"
                  class="py-4"
                >
                  <span class="text-sm text-gray-900">
                    <template v-if="getActivityData(activity).emphasize">
                      {{ getActivityData(activity).textBefore }}
                      <em>{{ getActivityData(activity).emphasize }}</em>
                      {{ getActivityData(activity).textAfter }}
                    </template>
                    <template v-else>
                      {{ getActivityData(activity).text }}
                    </template>
                  </span>
                </TableCell>

                <!-- Person/Client Info -->
                <TableCell data-cy="cell-person">
                  <div
                    v-if="
                      (activity as any).case_client_display_name?.trim() ||
                      (activity as any).case_client_email?.trim()
                    "
                    class="flex items-center gap-3"
                  >
                    <div class="min-w-0 flex-1">
                      <div
                        data-cy="person-name"
                        class="truncate text-sm font-medium text-gray-900"
                      >
                        {{ (activity as any).case_client_display_name?.trim() || '-' }}
                      </div>
                      <div
                        v-if="(activity as any).case_client_email?.trim()"
                        data-cy="person-email"
                        class="truncate text-xs text-gray-500"
                      >
                        {{ (activity as any).case_client_email }}
                      </div>
                    </div>
                  </div>
                  <span
                    v-else
                    class="text-sm text-gray-400"
                  >
                    {{ t('NoClientInfo') }}
                  </span>
                </TableCell>

                <!-- Clerk (Sachbearbeiter) -->
                <TableCell
                  data-cy="cell-clerk"
                  class="py-4"
                >
                  <div
                    v-if="getClerkName(activity)"
                    data-cy="clerk-badge"
                    class="group/clerk relative inline-flex max-w-[220px] items-center gap-2 rounded-full border
                      border-gray-300 bg-white py-1 pl-1 pr-3"
                    :title="getClerkName(activity) ?? ''"
                  >
                    <span
                      class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-medium
                        text-white"
                    >
                      {{ getClerkInitials(activity) }}
                    </span>
                    <span class="truncate text-sm font-medium text-gray-900">
                      {{ getClerkName(activity) }}
                    </span>
                  </div>
                  <span
                    v-else
                    data-cy="clerk-unassigned"
                    class="text-sm text-gray-400"
                  >
                    {{ t('NotAssigned') }}
                  </span>
                </TableCell>

                <!-- Action -->
                <TableCell class="py-4">
                  <Button
                    data-cy="btn-open-case"
                    variant="secondary"
                    class="flex h-8 items-center gap-1 border border-gray-300 bg-white text-sm hover:bg-gray-200"
                    @click.stop="handleOpenCase(activity)"
                  >
                    <ExternalLink class="h-3 w-3" />
                    {{ t('OpenCase') }}
                  </Button>
                </TableCell>
              </TableRow>
            </template>
          </PaginatedTableView>
        </div>
      </div>
    </div>
  </SpaCompatComponent>
</template>
